import React, { useEffect, useState } from "react";
import { validRegions } from "./DxfRegionSelector";
import { applyLayout, makeLayout, applyImageLayout, makeImageLayout, readLayouts, writeLayouts, fetchLayouts, saveLayout, deleteLayout } from "./dxfLayoutStorage";

export default function DxfLayouts({ scene, regions, units, onChange, disabled, imageSize, canSave = true, namePrefix = "" }) {
  const [layouts, setLayouts] = useState([]);
  const [selected, setSelected] = useState("");
  const [name, setName] = useState("");
  const [align, setAlign] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [readable, setReadable] = useState(false);
  const [working, setWorking] = useState(false);
  const [legacy, setLegacy] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetchLayouts().then(data => { if (!cancelled) { setLayouts(data); setReadable(true); } })
      .catch(() => { if (!cancelled) setError("A szerver sablonjai nem tölthetők be. Próbáld újra."); });
    try { setLegacy(readLayouts()); } catch { setError("A régi helyi sablonok nem olvashatók; a szerver sablonjai ettől még használhatók."); }
    return () => { cancelled = true; };
  }, []);
  const action = async fn => {
    setError(""); setMessage(""); setWorking(true);
    try { await fn(); } catch (e) { setError(e.response?.data?.message || e.message || "A sablon művelete nem sikerült."); }
    finally { setWorking(false); }
  };
  return <fieldset disabled={disabled || working} className="border rounded p-3 mt-3">
    <legend className="float-none w-auto fs-6">Darabolási sablonok</legend>
    <p className="text-muted">A sablonokat a szerveren tároljuk, így másik gépről is használhatók. A darabolóhoz hozzáférő felhasználók közös sablonlistát látnak.</p>
    <button type="button" className="btn btn-outline-secondary" onClick={() => action(async () => {
      setLayouts(await fetchLayouts()); setReadable(true);
    })}>Sablonok újratöltése</button>
    {legacy.length > 0 && <button type="button" className="btn btn-outline-primary mb-2" disabled={!readable} onClick={() => action(async () => {
      let remaining = [...legacy];
      for (const layout of legacy) {
        const saved = await saveLayout(layout);
        setLayouts(rows => [...rows.filter(l => l.id !== saved.id), saved]);
        remaining = remaining.filter(l => l.id !== layout.id);
        writeLayouts(remaining); setLegacy(remaining);
      }
      setMessage("A helyi sablonok átkerültek a szerverre.");
    })}>Helyi sablonok átmásolása a szerverre ({legacy.length})</button>}
    <div className="d-flex flex-wrap gap-2 align-items-end">
      <label>Sablon neve<input className="form-control" maxLength={150} value={name} onChange={e => setName(e.target.value)} /></label>
      <button type="button" className="btn btn-outline-primary" disabled={!readable || !canSave || !name.trim() || !validRegions(regions)} onClick={() => action(async () => {
        const current = await fetchLayouts();
        if (current.some(l => l.name.toLocaleLowerCase() === name.trim().toLocaleLowerCase())) throw new Error("Ez a sablonnév már létezik. Adj meg másik nevet.");
        const templateRegions = namePrefix ? regions.map(r => ({ ...r,
          name: r.name.startsWith(`${namePrefix}-`) ? r.name.slice(namePrefix.length + 1) : r.name })) : regions;
        const layout = imageSize ? makeImageLayout(name, templateRegions, imageSize) : makeLayout(name, templateRegions, units, scene);
        const saved = await saveLayout(layout); setLayouts([...current, saved]); setSelected(saved.id);
        setMessage("A darabolási sablon elmentve.");
      })}>Kijelölések mentése sablonként</button>
    </div>
    <div className="d-flex flex-wrap gap-2 align-items-end mt-3">
      <label>Mentett sablon<select className="form-select" value={selected} onChange={e => setSelected(e.target.value)}>
        <option value="">Válassz sablont…</option>
        {layouts.map(l => <option key={l.id} value={l.id}>{l.name} ({l.regions.length} rész)</option>)}
      </select></label>
      <button type="button" className="btn btn-outline-primary" disabled={!selected} onClick={() => action(async () => {
        const current = await fetchLayouts(); setLayouts(current);
        const layout = current.find(l => l.id === selected);
        const next = imageSize ? applyImageLayout(layout, imageSize) : applyLayout(layout, units, scene, align);
        onChange(namePrefix ? next.map(r => ({ ...r, name: `${namePrefix}-${r.name}` })) : next);
        const outside = !imageSize && next.some(r => r.rect[0] < scene.min[0] || r.rect[1] < scene.min[1] || r.rect[2] > scene.max[0] || r.rect[3] > scene.max[1]);
        setMessage(outside ? "Sablon betöltve. Egyes keretek túlnyúlnak a rajzon; ellenőrizd és igazítsd őket darabolás előtt." : "Sablon betöltve. A keretek és nevek szabadon módosíthatók darabolás előtt.");
      })}>Sablon betöltése</button>
      <button type="button" className="btn btn-outline-danger" disabled={!selected} onClick={() => action(async () => {
        await deleteLayout(selected);
        setLayouts(rows => rows.filter(l => l.id !== selected)); setSelected(""); setMessage("Sablon törölve; a jelenlegi kijelölések megmaradtak.");
      })}>Sablon törlése</button>
    </div>
    {!imageSize && <>
      <label className="mt-2 d-block"><input type="checkbox" checked={align} onChange={e => setAlign(e.target.checked)} /> Igazítás az új rajz bal alsó sarkához</label>
      <small className="text-muted">DXF-sablonnál a fizikai méret megmarad. Képről mentett sablonnál a teljes rajzhoz méretezzük a kereteket.</small>
    </>}
    <small className="text-muted d-block">Betöltéskor a jelenlegi kijelölések lecserélődnek; utána a keretek és nevek szerkeszthetők.</small>
    {error && <p role="alert" className="text-danger mt-2">{error}</p>}
    {message && <p role="status" className="mt-2">{message}</p>}
  </fieldset>;
}
